import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchImageMetadata } from '../../api/Image';
import { fetchEventByImageKey } from '../../api/Event';
import { EventData } from '../../types/Event';
import { ImageDetails as ImageDetailsType } from '../../types/Image';
import ImageInspector from './ImageInspector';
import { Loader, Center, Text } from '@mantine/core';

interface ImageDetailsProps {
  onClose: () => void;
}

/**
 * Component that fetches both image metadata and event data before rendering the ImageInspector
 * This acts as a data fetching wrapper around the ImageInspector component
 */
const ImageDetails: React.FC<ImageDetailsProps> = ({ onClose }) => {
  const { imageKey } = useParams<{ imageKey: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [imageDetails, setImageDetails] = useState<ImageDetailsType | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!imageKey) {
        setError('No image key provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch both image metadata and event data in parallel
        const [imageResponse, eventResponse] = await Promise.all([
          fetchImageMetadata(imageKey),
          fetchEventByImageKey(imageKey)
        ]);

        setImageDetails(imageResponse.image);
        setEventData(eventResponse);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching image data:', err);
        setError('Failed to load image details. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [imageKey]);

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center style={{ height: '100vh' }}>
        <Text color="red" size="xl">{error}</Text>
      </Center>
    );
  }

  return (
    <ImageInspector 
      onClose={onClose} 
      eventData={eventData || undefined}
    />
  );
};

export default ImageDetails;
